import { APITypesV1 } from "@cur8/api-client";

export function limsManagedSamples(
  samples: readonly APITypesV1.BloodSample[] | undefined
): APITypesV1.BloodSample[] {
  return (samples ?? []).filter(
    (sample) => sample.analyticalFlow === APITypesV1.AnalyticalFlow.LimsManaged
  );
}

export function hasLimsManagedSamples(
  samples: readonly APITypesV1.BloodSample[] | undefined
): boolean {
  return limsManagedSamples(samples).length > 0;
}

// Draw-confirmation renders plasma's tubes first, then whole blood's.
const TUBE_TYPE_ORDER: readonly APITypesV1.SampleType[] = [
  APITypesV1.SampleType.Plasma,
  APITypesV1.SampleType.WholeBlood,
];

export const sampleTypeLabels: Record<APITypesV1.SampleType, string> = {
  [APITypesV1.SampleType.Plasma]: "Plasma",
  [APITypesV1.SampleType.WholeBlood]: "Whole Blood",
};

export function orderedTubes(
  samples: readonly APITypesV1.BloodSample[] | undefined
): APITypesV1.BloodSample[] {
  const bySampleType = new Map<
    APITypesV1.SampleType,
    APITypesV1.BloodSample[]
  >();
  for (const sample of limsManagedSamples(samples)) {
    const list = bySampleType.get(sample.sampleType);
    if (list) {
      list.push(sample);
    } else {
      bySampleType.set(sample.sampleType, [sample]);
    }
  }
  return TUBE_TYPE_ORDER.flatMap((type) => bySampleType.get(type) ?? []);
}

export type TubeResolution = "pending" | "drawn" | "skipped";

// The backend doesn't echo back why a tube was skipped, so the reason is
// only known locally for this session — not recoverable on reload.
export function resolutionFor(sample: APITypesV1.BloodSample): TubeResolution {
  const status = sample.preAnalyticalStatus;
  if (status == null) {
    return "pending";
  }
  return status === APITypesV1.PreAnalyticalStatus.NotDrawn
    ? "skipped"
    : "drawn";
}

// Nothing left for the operator to record: every tube the lab manages has a
// draw decision behind it. A skipped tube counts as decided, so a visit that
// skipped the lot has nothing to record either. No tubes at all is not
// "recorded", it is a visit the blood step has no business on.
export function everyTubeRecorded(
  samples: readonly APITypesV1.BloodSample[] | undefined
): boolean {
  const tubes = limsManagedSamples(samples);
  return (
    tubes.length > 0 && tubes.every((tube) => resolutionFor(tube) !== "pending")
  );
}

// The blood step and Scan progress both read their wording from here, so the
// same state can't end up worded two ways.
export type TubeStatusKind =
  | "rejected"
  | "missing"
  | "sentToLab"
  | "received"
  | "accepted"
  | "skipped";

// How far the lab has got with the tube. `none` is a draw outcome the tube card
// already states in words, so it earns no lab pill.
type LabStage = "none" | "sent" | "received" | "decided";

export type TubeStatus = {
  word: string;
  highlight?: "success" | "error";
  pending?: boolean;
  lab: LabStage;
};

// Keyed on the union, so a new kind has to state its wording and how far the
// lab has got with it, rather than dropping out of a hand-listed subset.
export const TUBE_STATUS: Record<TubeStatusKind, TubeStatus> = {
  rejected: { word: "rejected", highlight: "error", lab: "decided" },
  // Neutral, not an error: an undrawn tube is the normal state at the start of
  // every visit, and red here would compete with the one red worth acting on.
  missing: { word: "missing", lab: "none" },
  // Awaiting the lab's verdict rather than settled, so both spin.
  sentToLab: { word: "sent to lab", pending: true, lab: "sent" },
  received: { word: "received", pending: true, lab: "received" },
  accepted: { word: "accepted", highlight: "success", lab: "decided" },
  skipped: { word: "skipped", lab: "none" },
};

const STATUS_KIND: Record<APITypesV1.PreAnalyticalStatus, TubeStatusKind> = {
  [APITypesV1.PreAnalyticalStatus.Rejected]: "rejected",
  [APITypesV1.PreAnalyticalStatus.Accepted]: "accepted",
  [APITypesV1.PreAnalyticalStatus.Received]: "received",
  [APITypesV1.PreAnalyticalStatus.IdentityConfirmed]: "sentToLab",
  [APITypesV1.PreAnalyticalStatus.NotDrawn]: "skipped",
};

// Only a tube with no status at all is missing. A status this build doesn't
// know is still the lab holding the tube, so it waits on the lab rather than
// reading as a tube nobody has drawn.
export function tubeStatusKindFor(
  sample: APITypesV1.BloodSample
): TubeStatusKind {
  const status = sample.preAnalyticalStatus;
  if (status == null) {
    return "missing";
  }
  return STATUS_KIND[status] ?? "sentToLab";
}

export function isRejected(sample: APITypesV1.BloodSample): boolean {
  return sample.preAnalyticalStatus === APITypesV1.PreAnalyticalStatus.Rejected;
}

export type LabPill = {
  label: string;
  highlight?: "success" | "error";
  pending?: boolean;
};

// No count: this pill sits on a single tube's row.
export function labPillFor(
  sample: APITypesV1.BloodSample
): LabPill | undefined {
  const status = TUBE_STATUS[tubeStatusKindFor(sample)];
  if (status.lab === "none") {
    return undefined;
  }
  return {
    label: `tube ${status.word}`,
    highlight: status.highlight,
    pending: status.pending,
  };
}

// The boundary is receipt, not the draw: until the lab has picked a tube up
// there is no status to have appeared yet, however many have left the room.
export type LabProgress = "awaitingLab" | "labWorking" | "labFinished";

export function labProgressFor(
  samples: readonly APITypesV1.BloodSample[] | undefined
): LabProgress | undefined {
  // A skipped tube never reaches the lab, so it can't be waited on. A visit
  // that skipped every tube has no lab side to report at all.
  const stages = limsManagedSamples(samples)
    .map(tubeStatusKindFor)
    .filter((kind) => kind !== "skipped")
    .map((kind) => TUBE_STATUS[kind].lab);
  if (stages.length === 0) {
    return undefined;
  }
  if (!stages.some((stage) => stage === "received" || stage === "decided")) {
    return "awaitingLab";
  }
  return stages.every((stage) => stage === "decided")
    ? "labFinished"
    : "labWorking";
}

export type TubePill = {
  key: string;
  label: string;
  highlight?: "success" | "error";
  pending?: boolean;
};

const STATUS_ORDER: readonly TubeStatusKind[] = [
  "rejected",
  "missing",
  "sentToLab",
  "received",
  "accepted",
  "skipped",
];

// One identity for a type-and-status group, so the count it was tallied under
// and the key its pill renders with can't come apart.
const groupKey = (
  sampleType: APITypesV1.SampleType,
  kind: TubeStatusKind
): string => `${sampleType}-${kind}`;

// Tubes sharing a type and a status collapse into one counted pill, otherwise
// an extended plasma visit renders identical pills that read as a fault. Counts
// are always shown, so they add up to the tubes on the visit.
export function tubePills(
  samples: readonly APITypesV1.BloodSample[] | undefined
): TubePill[] {
  const counts = new Map<string, number>();
  for (const tube of limsManagedSamples(samples)) {
    const key = groupKey(tube.sampleType, tubeStatusKindFor(tube));
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return TUBE_TYPE_ORDER.flatMap((sampleType) =>
    STATUS_ORDER.flatMap((kind) => {
      const key = groupKey(sampleType, kind);
      const count = counts.get(key);
      if (count == null) {
        return [];
      }
      const status = TUBE_STATUS[kind];
      return [
        {
          key,
          label: `${count} ${sampleTypeLabels[sampleType]} ${status.word}`,
          highlight: status.highlight,
          pending: status.pending,
        },
      ];
    })
  );
}
